/**
 * 62.54 step 7, the look (ARCHITECTURE §5.6): weather seen from outside it. With the weather cells on (step 5's switch;
 * core/weatherCells.ts carries the weather at a place — full inside a cell's radius, gone a little beyond),
 * each of the planet's cells (core/windField.ts cellsAt) stands where it is as a few large soft volumes, so weather is
 * visible from across the valley and from the commander's view — not only as the air you stand in (weatherView.ts):
 *
 *   - **rain**: a grey curtain round the cell's footprint, streaked and falling, under a grey cloud cap;
 *   - **storm**: the same, darker and taller, its cap and curtain lit now and then by storm light (a flash in the shader,
 *     no light objects);
 *   - **fog**: a low pale wall at the bank's edge — the wall you see from across the valley;
 *   - **dust / ash**: a low tan (grey) wall, billowing, densest at its leading edge.
 *
 * One small ring geometry and one disc are shared; each cell's walls are placed on the planet in the vertex shader (the
 * footprint is the circle `radius` radians round its centre, extruded along the planet's up). The walls fade within a
 * few dozen people of the camera, so from inside a cell the walls dissolve into the air the weather view already draws.
 * At most `MAX_CELLS` cells, two draws each. Presentation only; nothing with the cells off. `cellLook` is pure.
 *
 * The cap reads as cloud from every stop (lane E, after 67.40's skies). It is billowed, with layered noise and a
 * ragged rim, not a circle, and denser toward the middle. Seen from below, its base is darker and flat, with light
 * coming through its thin parts and its rim; seen from above, its top is pale and sunlit. It dims at night and warms
 * at dusk, from the cell's own sun height, as the sky's gradient does (skyHaze.ts `daylight`, `dusk`; the sun is the
 * scene's main light).
 *
 * 67.48 (the owner: "when viewed from orbit / commander they're way too symmetrical and circular, from the inside I think
 * they're cool"): the walls and the cap's rim take a keyed, stable irregular outline — a few lobes of their own (keyed by
 * the cell's id, the same for its life), stretched along the way the cell drifts (its band's wind) with wisps trailing
 * downwind and a firmer edge upwind — so a front reads as weather from orbit, not a disc. `cellRim` is the outline (the
 * vertex shader's `rim` keeps step with it); its mean is about 1, so the drawn cell covers about the sim's circle, whose
 * footprint (core/weatherCells.ts) is unchanged. A vertex term on the existing meshes: no new draw.
 */
import * as THREE from 'three';
import type { GameState, PlanetState, Vec3 } from '../types';
import type { WeatherKind } from '../core/weather';
import { cellsAt, weatherTime } from '../core/windField';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { daylight, dusk } from './skyHaze';
import { getSurfaceRadiusRaw } from '../core/surface';

/** How a cell of a kind looks: its wall's colour, opacity and height (person-heights), its cap, its pattern. Null = not drawn (clear, haze). */
export interface CellLook { color: string; opacity: number; height: number; cap: null | { color: string; opacity: number }; pattern: 'streaks' | 'billow' | 'smooth'; flash: boolean }
const LOOKS: Partial<Record<WeatherKind, CellLook>> = {
  rain: { color: '#8f9aa6', opacity: 0.32, height: 260, cap: { color: '#9aa3ad', opacity: 0.55 }, pattern: 'streaks', flash: false },
  storm: { color: '#5d6672', opacity: 0.45, height: 340, cap: { color: '#4e5560', opacity: 0.7 }, pattern: 'streaks', flash: true },
  snow: { color: '#e9edf2', opacity: 0.35, height: 220, cap: { color: '#d8dde4', opacity: 0.55 }, pattern: 'streaks', flash: false },   // 74.11 (a): a pale curtain under a cloud cap
  fog: { color: '#d9dde2', opacity: 0.6, height: 28, cap: null, pattern: 'smooth', flash: false },
  dust: { color: '#c2a476', opacity: 0.5, height: 60, cap: null, pattern: 'billow', flash: false },
  ash: { color: '#7d7a76', opacity: 0.45, height: 50, cap: null, pattern: 'billow', flash: false },
};
export function cellLook(kind: WeatherKind): CellLook | null { return LOOKS[kind] ?? null; }

/** 74.11 (d): a wall's presence by day — how much denser (on its opacity), how shaded its base, how lit its crown, how dim at night (a share of its colour). The shader carries the numbers. */
export const WALL_DAY = { denser: 0.6, baseShade: 0.7, crownLight: 0.1, night: 0.35 };

/** At most this many cells drawn (cellsAt keeps four alive; the nearest first). */
export const MAX_CELLS = 4;
/** The walls fade out within this many person-heights of the camera (inside a cell you see the air, not the wall). */
export const NEAR_FADE_PEOPLE = 40;

const SEGMENTS = 144, ROWS = 6, DISC_RINGS = 8;   // 67.48: 144 round so a wisp's tip resolves

/**
 * 74.15 (DECISIONS 384, the owner: "some weird fog behavior that seems to happen on them"): the walls stand on the ground.
 * They stood on the sphere (R − 3 people to R + the kind's height): on a megaplanet the ground runs 70 people under R to 300
 * over it, so a fog bank was buried under 89 % of ochrus's ground — its top a flat cut across a mountain's face — and hung
 * as a ribbon ~100 people over thalia's valleys, a white-out from the diorama (sweeps/2026-10-30-74.15-mountains-look.md).
 * Now each wall's foot follows the ground round its outline (`GROUND_SAMPLES` round it, each the lowest of its neighbours,
 * so the foot stays under the ground between two), and the cap stands its height over the highest ground under the cell.
 * Sampled every `GROUND_EVERY_S` per cell, not every frame.
 */
export const GROUND_SAMPLES = 72, GROUND_EVERY_S = 0.5;

/**
 * 74.15: a cell's ground — round its outline (`rim(a)`: its radius share at angle a in the frame e1, e2) the ground's
 * height over R, each sample the lowest of it and its two neighbours; and the highest ground under the cell (the outline
 * and three rings inside it), over R. World units. Pure.
 */
export function cellGround(planet: Pick<PlanetState, 'heightmap' | 'basePositions' | 'radius'>, centre: Vec3, e1: Vec3, e2: Vec3, theta: number, rim: (a: number) => number): { foot: Float32Array; top: number } {
  const R = planet.radius;
  const at = (a: number, share: number): number => {
    const t = theta * share, c = Math.cos(t), sn = Math.sin(t), ca = Math.cos(a), sa = Math.sin(a);
    const x = c * centre.x + sn * (ca * e1.x + sa * e2.x), y = c * centre.y + sn * (ca * e1.y + sa * e2.y), z = c * centre.z + sn * (ca * e1.z + sa * e2.z);
    return getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, R, x, y, z) - R;
  };
  const ring = new Float32Array(GROUND_SAMPLES);
  let top = -Infinity;
  for (let i = 0; i < GROUND_SAMPLES; i++) { const a = (i / GROUND_SAMPLES) * Math.PI * 2; ring[i] = at(a, rim(a)); top = Math.max(top, ring[i]); }
  const foot = new Float32Array(GROUND_SAMPLES);
  for (let i = 0; i < GROUND_SAMPLES; i++) foot[i] = Math.min(ring[i], ring[(i + 1) % GROUND_SAMPLES], ring[(i + GROUND_SAMPLES - 1) % GROUND_SAMPLES]);
  top = Math.max(top, at(0, 0));
  for (const share of [1 / 3, 2 / 3]) for (let i = 0; i < 24; i++) { const a = (i / 24) * Math.PI * 2; top = Math.max(top, at(a, share * rim(a))); }
  return { foot, top };
}

function ringGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], idx: number[] = [];
  for (let r = 0; r <= ROWS; r++) for (let s = 0; s <= SEGMENTS; s++) pos.push((s / SEGMENTS) * Math.PI * 2, r / ROWS, 0);
  for (let r = 0; r < ROWS; r++) for (let s = 0; s < SEGMENTS; s++) {
    const a = r * (SEGMENTS + 1) + s, b = a + SEGMENTS + 1;
    idx.push(a, b, a + 1, a + 1, b, b + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));   // (angle round, height share, 0): placed in the shader
  g.setIndex(idx);
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);   // placed in the shader: never frustum-culled by the unit box
  return g;
}
function discGeometry(): THREE.BufferGeometry {
  const pos: number[] = [0, 0, 0], idx: number[] = [];
  for (let r = 1; r <= DISC_RINGS; r++) for (let s = 0; s <= SEGMENTS; s++) pos.push((s / SEGMENTS) * Math.PI * 2, 0, r / DISC_RINGS);
  for (let s = 0; s < SEGMENTS; s++) idx.push(0, 1 + s, 2 + s);
  for (let r = 1; r < DISC_RINGS; r++) for (let s = 0; s < SEGMENTS; s++) {
    const a = 1 + (r - 1) * (SEGMENTS + 1) + s, b = a + SEGMENTS + 1;
    idx.push(a, b, a + 1, a + 1, b, b + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));   // (angle round, 0, radius share)
  g.setIndex(idx);
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
  return g;
}

/**
 * 67.48: the cell's outline — its rim's radius as a share of its radius at angle `a` round its centre (in its frame:
 * e1 at 0, e2 at π/2). `seed` keys its lobes; `wind` the way it drifts in that frame (its length the strength, 0–1).
 * The vertex shader's `rim` (PLACE) is the same sum. Pure.
 */
export const CELL_RIM = { lobes: [[2, 0.13], [3, 0.1], [5, 0.06], [9, 0.035]] as const, stretch: 0.25, tail: 0.15, front: 0.1,
  /** the wisps: keyed streamers trailing downwind — within `spread` radians of it, `length` a share of the radius, `width` radians */
  wisps: { count: 3, spread: 0.5, length: [0.2, 0.55] as const, width: 0.13 } };
/** 67.48: a cell's keyed outline values from its seed — its lobes' phases, its wisps' offsets from downwind and lengths. Pure. */
export interface CellKeys { phase: [number, number, number, number]; off: [number, number, number]; len: [number, number, number] }
export function cellKeys(seed: number): CellKeys {
  const h = (n: number) => { const v = Math.sin(seed + n) * 43758.5453; return v - Math.floor(v); };
  const W = CELL_RIM.wisps;
  return {
    phase: [0, 1, 2, 3].map(i => 6.283 * h(i + 1)) as CellKeys['phase'],
    off: [0, 1, 2].map(i => (h(11 + i) - 0.5) * 2 * W.spread) as CellKeys['off'],
    len: [0, 1, 2].map(i => W.length[0] + (W.length[1] - W.length[0]) * h(21 + i)) as CellKeys['len'],
  };
}
export function cellRim(a: number, seed: number | CellKeys, wind: { x: number; y: number }): number {
  const key = typeof seed === 'number' ? cellKeys(seed) : seed;
  let s = 1;
  CELL_RIM.lobes.forEach(([k, amp], i) => { s += amp * Math.cos(k * a + key.phase[i]); });
  const w = Math.hypot(wind.x, wind.y);
  if (w > 1e-4) {
    const down = Math.atan2(wind.y, wind.x), d = Math.cos(a - down), W = CELL_RIM.wisps;
    s *= (1 + CELL_RIM.stretch * w * d * d) / (1 + CELL_RIM.stretch * w / 2);   // stretched along it, its mean kept
    let extra = CELL_RIM.tail * Math.pow(Math.max(d, 0), 3) - CELL_RIM.front * Math.max(-d, 0);
    let mean = CELL_RIM.tail * 2 / (3 * Math.PI) - CELL_RIM.front / Math.PI;   // the tail's and front's means round the circle
    for (let i = 0; i < W.count; i++) {
      const off = key.off[i], len = key.len[i];
      const da = Math.atan2(Math.sin(a - down - off), Math.cos(a - down - off));
      extra += len * Math.exp(-(da / W.width) * (da / W.width));
      mean += len * W.width * Math.sqrt(Math.PI) / (2 * Math.PI);
    }
    s += w * (extra - mean);   // its mean kept: the drawn cell covers about the sim's circle
  }
  return s;
}
const L = CELL_RIM.lobes, Wp = CELL_RIM.wisps, f = (x: number) => x.toFixed(4);
const PLACE = `
uniform vec3 uPlanet; uniform vec3 uCentre; uniform vec3 uE1; uniform vec3 uE2; uniform float uTheta; uniform float uR;
uniform float uBottom; uniform float uTop; uniform vec4 uPhase; uniform vec3 uOff; uniform vec3 uLen; uniform vec2 uWind; uniform float uIrregular;
uniform vec4 uGround[${GROUND_SAMPLES / 4}];
// 74.15: the ground's height over R round the outline at angle a (cellGround's foot, between its samples)
float groundSample(int i) { return uGround[i / 4][i % 4]; }
float groundAt(float a) {
  float x = mod(a, 6.28318531) / 6.28318531 * ${GROUND_SAMPLES}.0;
  int i0 = min(int(floor(x)), ${GROUND_SAMPLES - 1}), i1 = i0 + 1 >= ${GROUND_SAMPLES} ? 0 : i0 + 1;
  return mix(groundSample(i0), groundSample(i1), x - float(i0));
}
// 67.48: the outline's radius share at angle a (cellRim, kept in step; the cell's keys from cellKeys)
float rim(float a) {
  if (uIrregular < 0.5) return 1.0;
  float s = 1.0${L.map(([k, amp], i) => ` + ${f(amp)} * cos(${f(k)} * a + uPhase[${i}])`).join('')};
  float w = length(uWind);
  if (w > 1e-4) {
    float down = atan(uWind.y, uWind.x), d = cos(a - down);
    s *= (1.0 + ${f(CELL_RIM.stretch)} * w * d * d) / (1.0 + ${f(CELL_RIM.stretch / 2)} * w);
    float extra = ${f(CELL_RIM.tail)} * pow(max(d, 0.0), 3.0) - ${f(CELL_RIM.front)} * max(-d, 0.0);
    float mean = ${f(CELL_RIM.tail * 2 / (3 * Math.PI) - CELL_RIM.front / Math.PI)};
    for (int i = 0; i < ${Wp.count}; i++) {
      float off = uOff[i], len = uLen[i];
      float da = atan(sin(a - down - off), cos(a - down - off)) / ${f(Wp.width)};
      extra += len * exp(-da * da);
      mean += len * ${f(Wp.width * Math.sqrt(Math.PI) / (2 * Math.PI))};
    }
    s += w * (extra - mean);
  }
  return s;
}
vec3 onPlanet(float a, float frac, float h) {
  float t = uTheta * frac;
  vec3 d = normalize(cos(t) * uCentre + sin(t) * (cos(a) * uE1 + sin(a) * uE2));
  return uPlanet + d * (uR + h);
}`;
const SHADE = `
uniform vec3 uColor; uniform float uOpacity; uniform float uTime; uniform float uFlash; uniform vec3 uCam; uniform float uNear;
uniform float uPattern;
varying vec3 vWorld; varying vec2 vUv;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y); }`;

function wallMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, side: THREE.DoubleSide,
    uniforms: uniformsFresh(),
    vertexShader: `${PLACE}
varying vec3 vWorld; varying vec2 vUv;
void main() {
  vec3 w = onPlanet(position.x, rim(position.x), groundAt(position.x) + mix(uBottom, uTop, position.y));   // 67.48: the irregular outline; 74.15: on the ground
  vWorld = w; vUv = vec2(position.x, position.y);
  gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
}`,
    fragmentShader: `${SHADE}
uniform float uDay; uniform float uDusk;
void main() {
  float h = vUv.y;
  // 74.11 (d)'s finding: by day a wall was faint against the kind's own bright sky — its top now fades later, its base is shaded, its crown lit, and it is denser by day; at night it dims and at dusk it warms, as the cap does
  float a = smoothstep(0.0, 0.12, h) * (1.0 - smoothstep(mix(0.55, 0.78, uDay), 1.0, h));   // soft at the ground and fading up
  if (uPattern < 0.5) a *= 0.55 + 0.45 * smoothstep(0.35, 0.75, noise(vec2(vUv.x * 160.0, h * 6.0 + uTime * 2.2)));   // streaks, falling
  else if (uPattern < 1.5) a *= 0.6 + 0.4 * noise(vec2(vUv.x * 24.0 + uTime * 0.15, h * 3.0 - uTime * 0.1));       // billow
  a *= smoothstep(uNear * 0.4, uNear, distance(vWorld, uCam));                      // dissolves near the camera
  a *= 1.0 + ${WALL_DAY.denser.toFixed(2)} * uDay;
  vec3 c = uColor * mix(1.0, ${WALL_DAY.baseShade.toFixed(2)} + ${(1 - WALL_DAY.baseShade).toFixed(2)} * h, uDay);   // the base shaded by day, the crown full
  c += vec3(${WALL_DAY.crownLight.toFixed(2)}) * uDay * smoothstep(0.5, 1.0, h);                                        // the crown lit from above
  c *= ${WALL_DAY.night.toFixed(2)} + ${(1 - WALL_DAY.night).toFixed(2)} * uDay;                                         // night
  c += vec3(0.5, 0.24, 0.08) * uDusk * 0.5;                                                                             // dusk
  c *= 1.0 + 1.8 * uFlash;
  gl_FragColor = vec4(c, min(0.92, uOpacity * a));
}`,
  });
}
export function capMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, side: THREE.DoubleSide,
    uniforms: uniformsFresh(),
    vertexShader: `${PLACE}
varying vec3 vWorld; varying vec2 vUv;
void main() {
  vec3 w = onPlanet(position.x, position.z * rim(position.x), uTop);   // 67.48: the rim follows the outline
  vWorld = w; vUv = vec2(position.x, position.z);
  gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
}`,
    fragmentShader: `${SHADE}
uniform float uDay; uniform float uDusk; uniform vec3 uPlanet;
float fbm(vec2 p) { float v = 0.0, amp = 0.5; for (int i = 0; i < 4; i++) { v += amp * noise(p); p = p * 2.07 + vec2(13.1, 7.7); amp *= 0.5; } return v; }
void main() {
  float r = vUv.y;
  vec2 q = vec2(cos(vUv.x), sin(vUv.x)) * r;                                     // the disc's own plane, its radius 1
  float n = fbm(q * 4.0 + vec2(uTime * 0.015, -uTime * 0.01));                    // billows, drifting slowly
  float edge = 1.0 - smoothstep(0.62 + 0.3 * (n - 0.5), 1.0, r);                  // a ragged rim, not a circle
  float body = smoothstep(0.3, 0.62, n + 0.3 * (1.0 - r));                        // denser toward the middle
  float a = edge * mix(0.4, 1.0, body);                                           // thin parts stay cloud, not holes
  vec3 up = normalize(vWorld - uPlanet), toCam = normalize(uCam - vWorld);
  float below = 1.0 - smoothstep(-0.05, 0.05, dot(toCam, up));                   // the camera under the cap: its base
  vec3 col = uColor * mix(1.18, 0.7, below);                                       // the top pale and sunlit, the base dark and flat
  col = mix(col, uColor * 1.32 + vec3(0.05), below * (0.5 * (1.0 - body) + 0.45 * (1.0 - edge)));   // light through the thin parts and the rim
  col *= 0.22 + 0.78 * uDay;                                                       // night
  col += vec3(0.7, 0.34, 0.12) * uDusk * (0.4 + 0.6 * (1.0 - edge)) * mix(0.55, 1.2, below);   // dusk: the low sun warms it, its base and rim most
  col *= 1.0 + 1.2 * uFlash;
  a *= smoothstep(uNear * 0.4, uNear, distance(vWorld, uCam));
  gl_FragColor = vec4(col, uOpacity * a);
}`,
  });
}
function uniformsFresh(): Record<string, THREE.IUniform> {
  return {
    uPlanet: { value: new THREE.Vector3() }, uCentre: { value: new THREE.Vector3(0, 1, 0) }, uE1: { value: new THREE.Vector3(1, 0, 0) }, uE2: { value: new THREE.Vector3(0, 0, 1) },
    uTheta: { value: 0.3 }, uR: { value: 1 }, uBottom: { value: 0 }, uTop: { value: 1 },
    uColor: { value: new THREE.Color() }, uOpacity: { value: 0 }, uTime: { value: 0 }, uFlash: { value: 0 }, uCam: { value: new THREE.Vector3() }, uNear: { value: 1 }, uPattern: { value: 0 },
    uDay: { value: 1 }, uDusk: { value: 0 },
    uPhase: { value: new THREE.Vector4() }, uOff: { value: new THREE.Vector3() }, uLen: { value: new THREE.Vector3() }, uWind: { value: new THREE.Vector2() }, uIrregular: { value: 1 },   // 67.48
    uGround: { value: Array.from({ length: GROUND_SAMPLES / 4 }, () => new THREE.Vector4()) },   // 74.15
  };
}

/** 67.48: seconds ahead a cell is looked at to know which way it drifts; how stretched a cell in its prime is (0–1). */
export const CELL_LOOK_AHEAD_S = 30, CELL_WIND_STRENGTH = 0.8;
/** 67.48: a cell's id as its outline's seed (spread so neighbouring ids differ). Pure. */
export function cellSeed(id: number): number { return (id * 12.9898) % 1000; }

/** The tangent frame round a cell's centre (unit): two directions across it. Pure. */
export function cellFrame(centre: Vec3): { e1: Vec3; e2: Vec3 } {
  const ref = Math.abs(centre.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1 = { x: ref.y * centre.z - ref.z * centre.y, y: ref.z * centre.x - ref.x * centre.z, z: ref.x * centre.y - ref.y * centre.x };
  const l = Math.hypot(e1.x, e1.y, e1.z) || 1;
  const u = { x: e1.x / l, y: e1.y / l, z: e1.z / l };
  return { e1: u, e2: { x: centre.y * u.z - centre.z * u.y, y: centre.z * u.x - centre.x * u.z, z: centre.x * u.y - centre.y * u.x } };
}

interface Slot { wall: THREE.Mesh; cap: THREE.Mesh }

export class CellVolumes {
  private readonly slots: Slot[] = [];
  private readonly ring = ringGeometry();
  private readonly disc = discGeometry();
  private time = 0;
  private flash = 0;
  private nextFlash = 6;
  /** how many cells were drawn last frame (for the cost statement and tests) */
  drawn = 0;
  /** 67.48: the irregular outline (on); off draws the circles (the sheet's before) */
  irregular = true;
  /** 67.48: each cell's drift in its frame, by id (kept when it fades past the look-ahead) */
  private readonly drift = new Map<number, { x: number; y: number }>();
  /** 74.15: each cell's ground (cellGround) by id, and when it was sampled (this.time) */
  private readonly ground = new Map<number, { at: number; foot: Float32Array; top: number }>();
  /** the sun: the scene's main light (read each frame since 74.11 (d): 74.2's clock turns it through skyHaze.setSun, and a cap's day and a wall's shade follow) */
  private readonly sunDir = new THREE.Vector3(40, 50, 30).normalize();
  private sunLight: THREE.DirectionalLight | null = null;

  constructor(private readonly scene: THREE.Scene) {
    let sun: THREE.DirectionalLight | null = null;
    scene.traverse(o => { const l = o as THREE.DirectionalLight; if (l.isDirectionalLight && (!sun || l.intensity > sun.intensity)) sun = l; });
    if (sun) { this.sunLight = sun; this.sunDir.copy((sun as THREE.DirectionalLight).position).normalize(); }
    for (let i = 0; i < MAX_CELLS; i++) {
      const wall = new THREE.Mesh(this.ring, wallMaterial()), cap = new THREE.Mesh(this.disc, capMaterial());
      wall.frustumCulled = cap.frustumCulled = false;
      wall.visible = cap.visible = false;
      wall.renderOrder = cap.renderOrder = 2;
      scene.add(wall, cap);
      this.slots.push({ wall, cap });
    }
  }

  /** Each frame: the cells of the planet `planetIndex` at the action clock's `seconds`, seen by `camera`. Hidden with the cells off. */
  update(state: GameState, planetIndex: number, seconds: number, camera: THREE.Camera, dt: number): void {
    this.time += dt;
    if (this.sunLight) this.sunDir.copy(this.sunLight.position).normalize();   // the clock's sun
    const on = (state.config as typeof state.config & { weatherCells?: boolean }).weatherCells === true;
    const planet: PlanetState | undefined = planetIndex >= 0 ? state.planets[planetIndex] : undefined;
    const cells = on && planet ? cellsAt(state, planetIndex, weatherTime(state, seconds)).filter(c => cellLook(c.kind)) : [];
    // 67.48: the way each cell drifts (its band's wind): where it will be a little later, by id
    if (this.irregular && on && planet && cells.length) {
      const ahead = new Map(cellsAt(state, planetIndex, weatherTime(state, seconds + CELL_LOOK_AHEAD_S)).map(c => [c.id, c]));
      const live = new Set(cells.map(c => c.id));
      for (const id of this.drift.keys()) if (!live.has(id)) this.drift.delete(id);   // gone cells forgotten
      for (const c of cells) {
        const a = c.id !== undefined ? ahead.get(c.id) : undefined;
        if (!a || c.id === undefined) continue;
        const { e1, e2 } = cellFrame(c.centre), dx = a.centre.x - c.centre.x, dy = a.centre.y - c.centre.y, dz = a.centre.z - c.centre.z;
        const x = dx * e1.x + dy * e1.y + dz * e1.z, y = dx * e2.x + dy * e2.y + dz * e2.z, l = Math.hypot(x, y);
        if (l > 1e-12) this.drift.set(c.id, { x: x / l, y: y / l });
      }
    }
    // storm light: now and then while a storm is drawn
    const storm = cells.some(c => c.kind === 'storm');
    this.flash = Math.max(0, this.flash - dt * 3.5);
    if (storm && (this.nextFlash -= dt) <= 0) { this.flash = 1; this.nextFlash = 4 + Math.random() * 9; }
    const person = planet ? PERSON_BODY_HEIGHT * personSetOf(state).k : 1;
    const cam = new THREE.Vector3(); camera.getWorldPosition(cam);
    this.drawn = Math.min(cells.length, MAX_CELLS);
    this.slots.forEach((slot, i) => {
      const c = cells[i];
      const look = c ? cellLook(c.kind) : null;
      slot.wall.visible = !!look;
      slot.cap.visible = !!look?.cap;
      if (!c || !look || !planet) return;
      const { e1, e2 } = cellFrame(c.centre);
      for (const [mesh, colour, opacity] of [[slot.wall, look.color, look.opacity], [slot.cap, look.cap?.color ?? look.color, look.cap?.opacity ?? 0]] as const) {
        const u = (mesh.material as THREE.ShaderMaterial).uniforms;
        u.uPlanet.value.set(planet.position.x, planet.position.y, planet.position.z);
        u.uCentre.value.set(c.centre.x, c.centre.y, c.centre.z);
        u.uE1.value.set(e1.x, e1.y, e1.z); u.uE2.value.set(e2.x, e2.y, e2.z);
        u.uTheta.value = c.radius; u.uR.value = planet.radius;
        u.uBottom.value = -3 * person; u.uTop.value = look.height * person;
        (u.uColor.value as THREE.Color).set(colour);
        u.uOpacity.value = opacity * c.ease; u.uTime.value = this.time;   // born and fading with its weather (step 5's ease)
        u.uFlash.value = look.flash ? this.flash : 0;
        u.uCam.value.copy(cam); u.uNear.value = NEAR_FADE_PEOPLE * person;
        u.uPattern.value = look.pattern === 'streaks' ? 0 : look.pattern === 'billow' ? 1 : 2;
        const el = c.centre.x * this.sunDir.x + c.centre.y * this.sunDir.y + c.centre.z * this.sunDir.z;   // the sun's height over the cell
        u.uDay.value = daylight(el); u.uDusk.value = dusk(el);
        // 67.48: its keyed outline, stretched the way it drifts (as strong as the cell is in its prime)
        const dr = c.id !== undefined ? this.drift.get(c.id) : undefined, strength = CELL_WIND_STRENGTH * c.ease;
        const key = cellKeys(cellSeed(c.id ?? i));
        (u.uPhase.value as THREE.Vector4).fromArray(key.phase); (u.uOff.value as THREE.Vector3).fromArray(key.off); (u.uLen.value as THREE.Vector3).fromArray(key.len);
        (u.uWind.value as THREE.Vector2).set((dr?.x ?? 0) * strength, (dr?.y ?? 0) * strength);
        u.uIrregular.value = this.irregular ? 1 : 0;
      }
      // 74.15: the wall's foot on the ground round its outline, the cap its height over the highest ground under it
      const id = c.id ?? -1 - i;
      let gr = this.ground.get(id);
      if (!gr || this.time - gr.at >= GROUND_EVERY_S) {
        const wu = (slot.wall.material as THREE.ShaderMaterial).uniforms, w = wu.uWind.value as THREE.Vector2, key = cellKeys(cellSeed(c.id ?? i));
        const rim = this.irregular ? (a: number) => cellRim(a, key, { x: w.x, y: w.y }) : () => 1;
        gr = { at: this.time, ...cellGround(planet, c.centre, e1, e2, c.radius, rim) };
        this.ground.set(id, gr);
      }
      const gu = (slot.wall.material as THREE.ShaderMaterial).uniforms.uGround.value as THREE.Vector4[];
      for (let k = 0; k < gu.length; k++) gu[k].set(gr.foot[4 * k], gr.foot[4 * k + 1], gr.foot[4 * k + 2], gr.foot[4 * k + 3]);
      (slot.cap.material as THREE.ShaderMaterial).uniforms.uTop.value = gr.top + look.height * person;
    });
    const live = new Set(cells.slice(0, this.drawn).map((c, i) => c.id ?? -1 - i));
    for (const id of this.ground.keys()) if (!live.has(id)) this.ground.delete(id);
  }

  dispose(): void {
    for (const s of this.slots) { this.scene.remove(s.wall, s.cap); (s.wall.material as THREE.Material).dispose(); (s.cap.material as THREE.Material).dispose(); }
    this.ring.dispose(); this.disc.dispose();
  }
}
